<template>
  <div class="badge" :class="[props.variant, props.size]">
    <div class="dot" v-if="props.dot"></div>
    <div class="label">{{ props.label }}</div>
  </div>
</template>

<script setup lang="ts">
import { BadgeVariant } from './badge-variant.type';

interface BadgeProps {
  label?: string;
  variant?: BadgeVariant;
  dot?: boolean;
  size?: 'small' | 'medium';
}

const props = withDefaults(defineProps<BadgeProps>(), {
  variant: 'ghost',
  dot: false,
  size: 'medium',
});
</script>

<style scoped lang="scss">
.badge {
  display: flex;
  align-items: center;
  gap: var(--t-spacing-xs);

  padding: var(--t-spacing-xs) var(--t-spacing-sm);

  width: fit-content;
  max-height: fit-content;

  border-radius: var(--t-border-radius-xs);

  .dot {
    width: var(--t-spacing-sm);
    height: var(--t-spacing-sm);
    border-radius: var(--t-border-radius-half-rounded);
  }

  .label {
    color: var(--t-color-neutral-900);
    font: var(--t-typography-label-md-font);
  }

  &.small {
    .label {
      font: var(--t-typography-label-sm-font);
    }
  }

  &.orange {
    background-color: var(--t-color-orange-200);

    .dot {
      background-color: var(--t-color-orange-400);
    }
  }

  &.green {
    background-color: var(--t-color-green-200);

    .dot {
      background-color: var(--t-color-green-500);
    }
  }

  &.blue {
    background-color: var(--t-color-blue-200);

    .dot {
      background-color: var(--t-color-blue-500);
    }
  }

  &.violet {
    background-color: var(--t-color-violet-200);

    .dot {
      background-color: var(--t-color-violet-500);
    }
  }

  &.yellow {
    background-color: var(--t-color-yellow-200);

    .dot {
      background-color: var(--t-color-yellow-400);
    }
  }

  &.red {
    background-color: var(--t-color-red-200);

    .dot {
      background-color: var(--t-color-red-400);
    }
  }

  &.grey {
    background-color: var(--t-color-neutral-200);

    .dot {
      background-color: var(--t-color-neutral-500);
    }
  }

  &.grey-dark {
    background-color: var(--t-text-color-tertiary);

    .dot {
      background-color: var(--t-color-neutral-400);
    }

    .label {
      color: var(--t-color-neutral-100);
    }
  }
}
</style>
